import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { site } from "../lib/siteConfig";
import { Copy, Check, Download, MessageCircle, Mail, Instagram, Globe } from "lucide-react";

/* -------- Brand palette -------- */
const palette = [
  { name: "Ink (Blu profondo)", hex: "#0F172A", role: "Sfondi primari · Titoli" },
  { name: "Ink Hover", hex: "#1E293B", role: "Hover · Contrasti scuri" },
  { name: "Cream (Sfondo)", hex: "#FDFBF7", role: "Sfondo pagina · Zone luminose" },
  { name: "Beige chiaro", hex: "#E4DCCF", role: "Accenti · Bande decorative" },
  { name: "Beige scuro", hex: "#C9BFA8", role: "Dettagli · Bordi eleganti" },
  { name: "Testo", hex: "#1A1A1A", role: "Testi principali" },
  { name: "Muted", hex: "#5C5C5C", role: "Testi secondari · Caption" },
  { name: "WhatsApp Green", hex: "#25D366", role: "Accent CTA WhatsApp" },
];

const fonts = [
  {
    name: "Playfair Display",
    role: "Titoli · Nome · Numeri d'impatto",
    weights: "400, 700, 900 · Italic",
    google: "https://fonts.google.com/specimen/Playfair+Display",
    canva: "Su Canva: cerca 'Playfair Display' nel selettore font (già presente nella libreria)",
    sample: "Aa",
    className: "font-display",
  },
  {
    name: "Outfit",
    role: "Testi · Caption · Micro-copy",
    weights: "300, 400, 500, 600, 700",
    google: "https://fonts.google.com/specimen/Outfit",
    canva: "Su Canva: cerca 'Outfit' nel selettore font (già presente)",
    sample: "Aa",
    className: "font-sans",
  },
];

/* -------- Small UI helpers -------- */
const CopyChip = ({ text, testid }) => {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setOk(true);
      setTimeout(() => setOk(false), 1500);
    } catch {}
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-white/90 hover:bg-white text-brand-ink border border-brand-line transition-colors"
    >
      {ok ? <Check size={12} className="text-brand-success" /> : <Copy size={12} />}
      {text}
    </button>
  );
};

const DownloadBtn = ({ targetRef, filename, testid }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!targetRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(targetRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      const a = document.createElement("a");
      a.download = filename;
      a.href = dataUrl;
      a.click();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-brand-ink text-white text-xs font-medium px-3 py-2 rounded-sm hover:bg-brand-inkHover transition-colors disabled:opacity-60"
    >
      <Download size={13} />
      {busy ? "..." : "Scarica PNG"}
    </button>
  );
};

/* -------- Template wrapper (fixed pixel size) -------- */
const TemplateFrame = ({ label, size, aspect, children, filename, testid }) => {
  const ref = useRef(null);
  return (
    <div className="template-card flex flex-col gap-3" data-testid={`tpl-${testid}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-muted font-semibold">
            {label}
          </div>
          <div className="text-[10px] text-brand-muted mt-0.5">{size}</div>
        </div>
        <DownloadBtn targetRef={ref} filename={filename} testid={`dl-${testid}`} />
      </div>
      <div
        className="template-preview"
        style={{
          aspectRatio: aspect,
          boxShadow: "0 20px 40px rgba(11, 26, 44, 0.14), 0 6px 14px rgba(11, 26, 44, 0.08)",
        }}
      >
        <div
          ref={ref}
          className="w-full h-full"
          style={{ backgroundColor: "#FDFBF7", overflow: "hidden" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

/* -------- 4 Post templates (1080x1080) -------- */
const PostQuote = () => (
  <div className="w-full h-full flex flex-col justify-between p-[6%] bg-brand-ink text-white relative overflow-hidden">
    <div
      aria-hidden
      className="absolute font-display italic font-black leading-none select-none pointer-events-none"
      style={{
        fontSize: "70%",
        color: "rgba(228, 220, 207, 0.09)",
        right: "-5%",
        bottom: "-8%",
        fontSize: "80cqh",
      }}
    >
      “
    </div>
    <div className="relative flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.6cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-beige"
        style={{ fontSize: "1.5cqi", letterSpacing: "0.28em" }}
      >
        Insight
      </div>
    </div>
    <div className="relative">
      <div
        className="uppercase font-semibold text-brand-beige mb-[3%]"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.28em" }}
      >
        <span className="inline-block align-middle h-px bg-brand-beige/70 w-8 mr-2" />
        Titolo sezione
      </div>
      <div
        className="font-display font-black leading-[0.98]"
        style={{ fontSize: "9cqi" }}
      >
        Titolo grande
        <br />
        <span className="italic font-normal">del tuo post.</span>
      </div>
      <div
        className="mt-[5%] text-white/60"
        style={{ fontSize: "2.2cqi", lineHeight: 1.4 }}
      >
        Sottotitolo / claim breve — modifica questo testo su Canva.
      </div>
    </div>
    <div className="relative flex items-center justify-between text-brand-beige/80" style={{ fontSize: "1.4cqi" }}>
      <span className="uppercase tracking-[0.28em] font-semibold">@gorlaproperties</span>
      <span className="uppercase tracking-[0.22em]">gorlaproperties.com</span>
    </div>
  </div>
);

const PostStat = () => (
  <div className="w-full h-full flex flex-col justify-between p-[6%] bg-brand-bg text-brand-ink relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.6cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-muted"
        style={{ fontSize: "1.5cqi", letterSpacing: "0.28em" }}
      >
        Numeri
      </div>
    </div>
    <div className="text-center">
      <div
        className="uppercase font-semibold text-brand-muted mb-[3%]"
        style={{ fontSize: "1.7cqi", letterSpacing: "0.32em" }}
      >
        Kpi · Metrica
      </div>
      <div
        className="font-display font-black leading-none text-brand-ink"
        style={{ fontSize: "24cqi" }}
      >
        00%
      </div>
      <div
        className="mt-[3%] font-display italic text-brand-muted"
        style={{ fontSize: "3.2cqi" }}
      >
        breve descrizione
      </div>
    </div>
    <div className="border-t border-brand-line pt-[3%] flex items-center justify-between" style={{ fontSize: "1.5cqi" }}>
      <span className="uppercase tracking-[0.28em] font-semibold text-brand-ink">Fonte / anno</span>
      <span className="uppercase tracking-[0.22em] text-brand-muted">gorlaproperties.com</span>
    </div>
  </div>
);

const PostSplit = () => (
  <div className="w-full h-full flex bg-brand-bg relative overflow-hidden">
    <div className="w-1/2 h-full flex flex-col justify-between p-[6%] bg-brand-beige/50 text-brand-ink">
      <div
        className="uppercase font-semibold text-brand-muted"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.32em" }}
      >
        Prima
      </div>
      <div className="text-center">
        <div className="uppercase text-brand-muted mb-1" style={{ fontSize: "1.4cqi", letterSpacing: "0.24em" }}>
          Titolo
        </div>
        <div className="font-display font-black leading-none" style={{ fontSize: "12cqi" }}>
          € 000
        </div>
        <div className="mt-2 text-brand-muted" style={{ fontSize: "1.6cqi" }}>
          netti / mese
        </div>
      </div>
      <div className="h-2" />
    </div>
    <div className="w-1/2 h-full flex flex-col justify-between p-[6%] bg-brand-ink text-white">
      <div
        className="uppercase font-semibold text-brand-beige text-right"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.32em" }}
      >
        Dopo
      </div>
      <div className="text-center">
        <div className="uppercase text-brand-beige mb-1" style={{ fontSize: "1.4cqi", letterSpacing: "0.24em" }}>
          Titolo
        </div>
        <div className="font-display font-black leading-none" style={{ fontSize: "12cqi" }}>
          € 000
        </div>
        <div className="mt-2 text-white/70" style={{ fontSize: "1.6cqi" }}>
          netti / mese
        </div>
      </div>
      <div
        className="text-right uppercase tracking-[0.22em] text-brand-beige/80"
        style={{ fontSize: "1.3cqi" }}
      >
        @gorlaproperties
      </div>
    </div>
  </div>
);

const PostQuestion = () => (
  <div className="w-full h-full flex flex-col justify-center p-[8%] bg-brand-bg text-brand-ink relative overflow-hidden">
    <div className="absolute top-[6%] left-[6%] right-[6%] flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.6cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-muted"
        style={{ fontSize: "1.5cqi", letterSpacing: "0.28em" }}
      >
        Domanda
      </div>
    </div>
    <div
      className="uppercase font-semibold text-brand-muted mb-[4%]"
      style={{ fontSize: "1.7cqi", letterSpacing: "0.28em" }}
    >
      <span className="inline-block align-middle h-px bg-brand-ink/50 w-10 mr-2" />
      Riflessione
    </div>
    <div
      className="font-display font-black leading-[1.02] text-brand-ink"
      style={{ fontSize: "9.5cqi" }}
    >
      Scrivi qui la <span className="italic font-normal">tua domanda</span> forte.
    </div>
    <div
      className="mt-[6%] text-brand-muted max-w-[85%]"
      style={{ fontSize: "2.4cqi", lineHeight: 1.5 }}
    >
      Aggiungi un paragrafo breve di supporto, con un tono diretto e umano.
    </div>
    <div className="absolute bottom-[6%] left-[6%] right-[6%] flex items-center justify-between text-brand-muted" style={{ fontSize: "1.4cqi" }}>
      <span className="uppercase tracking-[0.28em] font-semibold">@gorlaproperties</span>
      <span className="uppercase tracking-[0.22em]">gorlaproperties.com</span>
    </div>
  </div>
);

/* -------- 4 Story templates (1080x1920) -------- */
const StoryAnnounce = () => (
  <div className="w-full h-full flex flex-col justify-between p-[8%] bg-brand-ink text-white relative overflow-hidden">
    <div
      aria-hidden
      className="absolute font-display italic font-black leading-none select-none pointer-events-none"
      style={{
        color: "rgba(228, 220, 207, 0.08)",
        right: "-8%",
        bottom: "-4%",
        fontSize: "90cqh",
      }}
    >
      FG
    </div>
    <div className="relative flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.4cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-beige"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.28em" }}
      >
        News
      </div>
    </div>
    <div className="relative">
      <div
        className="uppercase font-semibold text-brand-beige mb-[4%]"
        style={{ fontSize: "1.8cqi", letterSpacing: "0.32em" }}
      >
        <span className="inline-block align-middle h-px bg-brand-beige/70 w-10 mr-2" />
        Annuncio
      </div>
      <div
        className="font-display font-black leading-[0.98]"
        style={{ fontSize: "11cqi" }}
      >
        Il tuo
        <br />
        <span className="italic font-normal">titolo forte</span>
        <br />
        qui.
      </div>
      <div
        className="mt-[6%] text-white/65 max-w-[85%]"
        style={{ fontSize: "2.4cqi", lineHeight: 1.5 }}
      >
        Sottotitolo o descrizione breve. Modifica su Canva.
      </div>
    </div>
    <div className="relative">
      <div
        className="bg-brand-beige text-brand-ink px-6 py-4 rounded-sm text-center font-display italic font-semibold"
        style={{ fontSize: "3.2cqi" }}
      >
        Call to action
      </div>
      <div
        className="mt-4 text-center uppercase tracking-[0.28em] text-brand-beige/80 font-semibold"
        style={{ fontSize: "1.5cqi" }}
      >
        @gorlaproperties
      </div>
    </div>
  </div>
);

const StoryStat = () => (
  <div className="w-full h-full flex flex-col justify-between p-[8%] bg-brand-bg text-brand-ink relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.4cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-muted"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.28em" }}
      >
        Numeri
      </div>
    </div>
    <div className="text-center">
      <div
        className="uppercase font-semibold text-brand-muted mb-[3%]"
        style={{ fontSize: "2cqi", letterSpacing: "0.32em" }}
      >
        Metrica
      </div>
      <div
        className="font-display font-black leading-none text-brand-ink"
        style={{ fontSize: "28cqi" }}
      >
        00%
      </div>
      <div
        className="mt-[4%] font-display italic text-brand-muted"
        style={{ fontSize: "3.4cqi" }}
      >
        breve descrizione
      </div>
    </div>
    <div className="text-center">
      <div className="border-t border-brand-line pt-[5%] text-brand-ink font-semibold" style={{ fontSize: "2cqi" }}>
        Fonte / anno / dato di riferimento
      </div>
      <div
        className="mt-3 uppercase tracking-[0.28em] text-brand-muted"
        style={{ fontSize: "1.4cqi" }}
      >
        gorlaproperties.com
      </div>
    </div>
  </div>
);

const StoryQuote = () => (
  <div className="w-full h-full flex flex-col justify-between p-[8%] bg-brand-bg text-brand-ink relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.4cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-muted"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.28em" }}
      >
        Voci
      </div>
    </div>
    <div className="relative">
      <div
        aria-hidden
        className="absolute font-display italic font-black leading-none text-brand-ink/8 pointer-events-none"
        style={{
          fontSize: "35cqi",
          color: "rgba(15, 23, 42, 0.08)",
          left: "-4%",
          top: "-20%",
        }}
      >
        “
      </div>
      <div
        className="relative font-display leading-[1.15] italic"
        style={{ fontSize: "5.5cqi" }}
      >
        Testimonianza / frase forte del cliente che modificherai su Canva.
      </div>
      <div className="mt-[6%] flex items-center gap-4">
        <div className="w-10 h-px bg-brand-ink/50" />
        <div>
          <div
            className="font-display font-bold text-brand-ink"
            style={{ fontSize: "2.4cqi" }}
          >
            Nome Cognome
          </div>
          <div
            className="text-brand-muted uppercase tracking-[0.24em]"
            style={{ fontSize: "1.3cqi" }}
          >
            Proprietario · Zona
          </div>
        </div>
      </div>
    </div>
    <div className="text-center uppercase tracking-[0.28em] text-brand-muted font-semibold" style={{ fontSize: "1.4cqi" }}>
      @gorlaproperties · gorlaproperties.com
    </div>
  </div>
);

const StoryCTA = () => (
  <div className="w-full h-full flex flex-col justify-between p-[8%] bg-brand-ink text-white relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="font-display leading-none" style={{ fontSize: "3.4cqi" }}>
        Gorla<span className="italic font-normal"> Properties</span>
      </div>
      <div
        className="uppercase font-semibold text-brand-beige"
        style={{ fontSize: "1.6cqi", letterSpacing: "0.28em" }}
      >
        Contatti
      </div>
    </div>
    <div>
      <div
        className="uppercase font-semibold text-brand-beige mb-[3%]"
        style={{ fontSize: "1.8cqi", letterSpacing: "0.32em" }}
      >
        <span className="inline-block align-middle h-px bg-brand-beige/70 w-10 mr-2" />
        Parliamone
      </div>
      <div
        className="font-display font-black leading-[0.98]"
        style={{ fontSize: "11cqi" }}
      >
        Parliamo del
        <br />
        <span className="italic font-normal">tuo immobile.</span>
      </div>
    </div>
    <div className="space-y-4 text-white/90" style={{ fontSize: "2.4cqi" }}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
          <MessageCircle size={14} />
        </div>
        <span className="font-semibold">{site.whatsappNumber}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
          <Mail size={14} />
        </div>
        <span>{site.email}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
          <Instagram size={14} />
        </div>
        <span>{site.instagramHandle}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
          <Globe size={14} />
        </div>
        <span className="font-semibold">gorlaproperties.com</span>
      </div>
    </div>
  </div>
);

/* -------- Facebook Cover (1640×624 · aspect 2.628 / 1) -------- */
const FBCoverMilano = () => (
  <div className="w-full h-full relative overflow-hidden bg-brand-ink text-white">
    {/* Background Milano photo */}
    <img
      src="https://images.pexels.com/photos/5771259/pexels-photo-5771259.jpeg"
      alt="Milano"
      crossOrigin="anonymous"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ filter: "saturate(0.85) contrast(1.05)" }}
    />
    {/* Gradient overlay for readability */}
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(90deg, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.72) 45%, rgba(15,23,42,0.15) 100%)",
      }}
    />

    {/* Content: kept in the center safe zone for mobile Facebook cover */}
    <div className="relative h-full w-full flex flex-col justify-between p-[3.5%]">
      {/* Top row */}
      <div className="flex items-start justify-end">
        <div
          className="uppercase font-semibold text-brand-beige text-right"
          style={{ fontSize: "1.2cqi", letterSpacing: "0.32em" }}
        >
          Consulenza Strategica sulla Rendita Immobiliare
        </div>
      </div>

      {/* Center headline (safe zone) */}
      <div className="max-w-[62%] pl-[2%]">
        <div
          className="font-display font-black leading-[0.98] text-white"
          style={{ fontSize: "6cqi" }}
        >
          La tua rendita,
          <br />
          <span className="italic font-normal">costruita con cura.</span>
        </div>
        <div
          className="mt-[1.5%] text-white/75 max-w-[85%]"
          style={{ fontSize: "1.4cqi", lineHeight: 1.5 }}
        >
          Affitti transitori · Brevi · 4+4 · Vendita
        </div>
      </div>

      {/* Bottom row */}
      <div className="flex items-end justify-between text-white/80">
        <div className="flex items-center gap-6" style={{ fontSize: "1.35cqi" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
              <MessageCircle size={11} strokeWidth={2.2} />
            </div>
            <span className="font-semibold">{site.whatsappNumber}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
              <Instagram size={11} strokeWidth={2.2} />
            </div>
            <span>{site.instagramHandle}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-brand-beige text-brand-ink flex items-center justify-center">
              <Globe size={11} strokeWidth={2.2} />
            </div>
            <span>gorlaproperties.com</span>
          </div>
        </div>
        <div
          className="uppercase tracking-[0.32em] font-semibold text-brand-beige"
          style={{ fontSize: "1.1cqi" }}
        >
        </div>
      </div>
    </div>
  </div>
);

/* ------------------- MAIN PAGE ------------------- */
export const BrandKit = () => {
  return (
    <div data-testid="brand-kit-page" className="min-h-screen bg-brand-bg text-brand-text font-sans">
      <style>{`
        .template-preview {
          width: 100%;
          border-radius: 4px;
          overflow: hidden;
          container-type: inline-size;
        }
        .template-preview > div {
          container-type: inline-size;
        }
      `}</style>

      {/* HEADER */}
      <header className="border-b border-brand-line/70 bg-brand-bg/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" data-testid="brandkit-home" className="text-sm text-brand-muted hover:text-brand-ink transition-colors">
            ← Torna al sito
          </a>
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-muted">
            Brand Kit
          </div>
          <div className="text-sm text-brand-muted hidden md:block">gorlaproperties.com</div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-brand-muted">
          <span className="inline-block align-middle h-px bg-brand-ink/40 w-10 mr-3" />
          Kit editabile su Canva
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] text-brand-ink max-w-3xl">
          Il tuo brand,
          <br />
          <span className="italic font-normal">pronto da personalizzare.</span>
        </h1>
        <p className="mt-6 text-brand-muted max-w-2xl text-base leading-relaxed">
          Palette, font e template Instagram (Post 1080×1080 + Story 1080×1920).
          Scarica il PNG, caricalo come sfondo in Canva, aggiungi i testi con i font ufficiali del brand.
        </p>
      </section>

      {/* PALETTE */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-ink">01 · Palette colori</h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
            Click HEX per copiare
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {palette.map((c, i) => (
            <div
              key={c.hex}
              data-testid={`palette-${i}`}
              className="rounded-sm overflow-hidden border border-brand-line bg-white flex flex-col"
            >
              <div className="h-24" style={{ background: c.hex }} />
              <div className="p-3 flex flex-col gap-2">
                <div className="font-display font-semibold text-brand-ink text-sm leading-tight">
                  {c.name}
                </div>
                <div className="text-[11px] text-brand-muted leading-tight">{c.role}</div>
                <CopyChip text={c.hex} testid={`copy-hex-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FONTS */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-ink">02 · Font ufficiali</h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
            Disponibili gratis su Canva & Google
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {fonts.map((f, i) => (
            <div
              key={f.name}
              data-testid={`font-${i}`}
              className="rounded-sm border border-brand-line bg-white p-6 flex gap-5 items-start"
            >
              <div
                className={`${f.className} text-brand-ink leading-none italic font-black`}
                style={{ fontSize: "70px" }}
              >
                {f.sample}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-brand-ink text-lg leading-tight">
                  {f.name}
                </div>
                <div className="text-xs text-brand-muted mt-0.5">{f.role}</div>
                <div className="mt-3 space-y-1 text-[12px] text-brand-ink">
                  <div>
                    <span className="text-brand-muted uppercase tracking-wider text-[10px]">Pesi: </span>
                    {f.weights}
                  </div>
                  <div className="pt-2">
                    <a
                      href={f.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-ink underline underline-offset-2 hover:text-brand-inkHover"
                      data-testid={`font-link-${i}`}
                    >
                      Apri su Google Fonts →
                    </a>
                  </div>
                  <div className="text-[11px] text-brand-muted mt-1">{f.canva}</div>
                  <div className="pt-2">
                    <CopyChip text={f.name} testid={`copy-font-${i}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INSTAGRAM POSTS */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-ink">
            03 · Instagram Post
          </h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
            1080 × 1080 · Quadrato
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <TemplateFrame
            label="Post · Insight"
            size="1080 × 1080"
            aspect="1 / 1"
            filename="post-insight.png"
            testid="post-insight"
          >
            <PostQuote />
          </TemplateFrame>
          <TemplateFrame
            label="Post · Numero d'impatto"
            size="1080 × 1080"
            aspect="1 / 1"
            filename="post-stat.png"
            testid="post-stat"
          >
            <PostStat />
          </TemplateFrame>
          <TemplateFrame
            label="Post · Prima / Dopo"
            size="1080 × 1080"
            aspect="1 / 1"
            filename="post-split.png"
            testid="post-split"
          >
            <PostSplit />
          </TemplateFrame>
          <TemplateFrame
            label="Post · Domanda"
            size="1080 × 1080"
            aspect="1 / 1"
            filename="post-question.png"
            testid="post-question"
          >
            <PostQuestion />
          </TemplateFrame>
        </div>
      </section>

      {/* INSTAGRAM STORIES */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-ink">
            04 · Instagram Story
          </h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
            1080 × 1920 · Verticale 9:16
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <TemplateFrame
            label="Story · Annuncio"
            size="1080 × 1920"
            aspect="9 / 16"
            filename="story-announce.png"
            testid="story-announce"
          >
            <StoryAnnounce />
          </TemplateFrame>
          <TemplateFrame
            label="Story · Numero"
            size="1080 × 1920"
            aspect="9 / 16"
            filename="story-stat.png"
            testid="story-stat"
          >
            <StoryStat />
          </TemplateFrame>
          <TemplateFrame
            label="Story · Testimonianza"
            size="1080 × 1920"
            aspect="9 / 16"
            filename="story-quote.png"
            testid="story-quote"
          >
            <StoryQuote />
          </TemplateFrame>
          <TemplateFrame
            label="Story · CTA Contatti"
            size="1080 × 1920"
            aspect="9 / 16"
            filename="story-cta.png"
            testid="story-cta"
          >
            <StoryCTA />
          </TemplateFrame>
        </div>
      </section>

      {/* FACEBOOK COVER */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-ink">
            05 · Copertina Facebook
          </h2>
          <span className="text-[11px] uppercase tracking-[0.28em] text-brand-muted">
            1640 × 624 · Full page cover
          </span>
        </div>
        <TemplateFrame
          label="Facebook · Cover Milano"
          size="1640 × 624 (safe zone al centro per mobile)"
          aspect="1640 / 624"
          filename="facebook-cover-milano.png"
          testid="fb-cover"
        >
          <FBCoverMilano />
        </TemplateFrame>
        <p className="mt-4 text-xs text-brand-muted leading-relaxed max-w-2xl">
          <strong className="text-brand-ink">Come caricarla:</strong> vai sulla tua pagina Facebook → 
          click sulla copertina → “Aggiorna copertina” → carica il PNG scaricato. 
          Facebook mostra tutto il banner su desktop e ritaglia i lati su mobile — il testo principale 
          è nella zona centrale sicura.
        </p>
      </section>

      {/* HOW TO */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <h2 className="font-display text-2xl font-bold text-brand-ink mb-6">
          06 · Come usarli su Canva
        </h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Scarica", d: "Clicca 'Scarica PNG' sul template che ti serve." },
            { n: "02", t: "Nuovo design", d: "In Canva crea un Post (1080×1080) o Story (1080×1920)." },
            { n: "03", t: "Carica lo sfondo", d: "Trascina il PNG come sfondo e blocca il livello." },
            { n: "04", t: "Aggiungi testo", d: "Usa Playfair Display + Outfit e i colori HEX del brand." },
          ].map((s) => (
            <div key={s.n} className="border border-brand-line bg-white p-5 rounded-sm">
              <div className="font-display font-black text-brand-ink text-3xl leading-none">
                {s.n}
              </div>
              <div className="mt-3 font-display font-semibold text-brand-ink">{s.t}</div>
              <div className="mt-1 text-sm text-brand-muted leading-snug">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-brand-line py-8 text-center text-xs text-brand-muted">
        Gorla Properties · Brand Kit
      </footer>
    </div>
  );
};

export default BrandKit;
